import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PopupModal from './PopupModal';
import './FloatingFeatures.css';

export interface FeatureItem {
    title: string;
    image: string;
    detailImage: string;
    firstText: string;
    secondText: string;
}

export interface Section {
    sectionImage: string;
    features: FeatureItem[];
}

interface FloatingFeaturesWidgetProps {
    sections: Section[];
}

export default function FloatingFeaturesWidget({ sections }: FloatingFeaturesWidgetProps) {
    const [modalIndex, setModalIndex] = useState<number | null>(null);
    const [sectionIndex, setSectionIndex] = useState<number | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const el = document.createElement('div');
        el.id = 'ff-modal-portal';
        document.body.appendChild(el);
        portalRef.current = el;
        return () => {
            document.body.removeChild(el);
        };
    }, []);

    const handleLearnMoreClick = (section: number, index: number) => {
        setModalIndex(index);
        setSectionIndex(section);

        setTimeout(() => {
            if (modalRef.current) {
                modalRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    const closeModal = () => {
        setModalIndex(null);
        setSectionIndex(null);
    };

    const showPreviousImage = () => {
        const currentSection = sections[sectionIndex ?? 0];
        if (modalIndex !== null && currentSection) {
            const len = currentSection.features.length;
            setModalIndex((modalIndex - 1 + len) % len);
        }
    };

    const showNextImage = () => {
        const currentSection = sections[sectionIndex ?? 0];
        if (modalIndex !== null && currentSection) {
            setModalIndex((modalIndex + 1) % currentSection.features.length);
        }
    };

    const activeFeature = modalIndex !== null && sectionIndex !== null
        ? sections[sectionIndex]?.features[modalIndex]
        : null;

    return (
        <div className="ff-main-container">
            {activeFeature && portalRef.current && createPortal(
                <div ref={modalRef}>
                    <PopupModal
                        bgImage={activeFeature.detailImage || activeFeature.image}
                        title={activeFeature.title}
                        firstText={activeFeature.firstText}
                        secondText={activeFeature.secondText}
                        onClose={closeModal}
                        onPrevious={showPreviousImage}
                        onNext={showNextImage}
                    />
                </div>,
                portalRef.current
            )}

            {sections.map((section, sIdx) => (
                <div
                    key={sIdx}
                    className="ff-section-container"
                    style={{ backgroundImage: `url(${section.sectionImage})` }}
                >
                    <div className="ff-section-wrapper">
                        {section.features.map((feature, fIdx) => (
                            <div className="ff-feature-card" key={fIdx}>
                                <img
                                    src={feature.image}
                                    alt={feature.title}
                                    loading="lazy"
                                />
                                <div className="ff-feature-data">
                                    <h5>{feature.title}</h5>
                                    <div
                                        className="ff-control-section"
                                        onClick={() => handleLearnMoreClick(sIdx, fIdx)}
                                    >
                                        <span className="ff-arrow-icon">&#8594;</span>
                                        <p className="ff-learn-more">Learn More</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
